import React from "react";
import { compact, has, isNumber, map } from "lodash";
import styled from "styled-components";
import { localize, shorten } from "@backpacker/number";
import { Spacing } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { Platform, getPlatformColor } from "@linkfluence/models/platform";
import Icon from "@ui-kit/icon";
import Tooltip from "@ui-kit/tooltip";
import { tr } from "@radarly/i18n";
import { Post, PostLensFactory } from "@radarly/models/posts";

interface IProps {
  post: Post;
  locale: Locale;
  className?: string;
  hideNullValues?: boolean;
}

const Scores = styled.div<{ color: string }>`
  line-height: 14px;
  font-size: 14px;
  color: ${(props) => props.color};
`;

const Score = styled.span`
  margin-left: ${Spacing.Medium};
`;

const StyledIcon = styled(Icon)`
  margin-right: 3px;
  stroke-width: 2px;
`;

const SCORES = {
  [Platform.Instagram]: {
    like: "heart",
    comment: "bubble-comment",
  },
  [Platform.Facebook]: {
    like: "reactions",
    comment: "bubble-comment",
    share: "share",
  },
  [Platform.Youtube]: {
    like: "like",
    dislike: "dislike",
    view: "eye",
    comment: "bubble-comment",
  },
  [Platform.Linkedin]: {
    like: "heart",
    comment: "bubble-comment",
  },
  [Platform.Vkontakte]: {
    like: "heart",
    repost: "share",
    comment: "bubble-comment",
  },
  [Platform.LittleRedBook]: {
    like: "like",
    star: "star",
    comment: "bubble-comment",
  },
  [Platform.Youku]: {
    like: "like",
    dislike: "dislike",
    view: "eye",
    comment: "bubble-comment",
  },
  [Platform.Gplus]: {
    plusone: "heart",
    repost: "share",
    comment: "bubble-comment",
  },
  [Platform.Dailymotion]: {
    views: "eye",
    comment: "bubble-comment",
  },
  [Platform.Sinaweibo]: {
    rt: "square-arrow",
    favourite: "like",
    reply: "bubble",
  },
  [Platform.Twitter]: {
    rt: "retweet",
    favourite: "heart",
    reply: "bubble",
  },
  [Platform.Wechat]: {
    like: "heart",
    read: "eye",
  },
  [Platform.Tiktok]: {
    view: "eye",
    like: "heart",
    comment: "bubble-comment",
    share: "share",
  },
  [Platform.Douyin]: {
    like: "heart",
    comment: "bubble-comment",
    repost: "share",
  },
  [Platform.Pinterest]: {
    save: "pin-plus",
    done: "camera",
  },
  [Platform.Reddit]: {
    comment: "bubble-comment",
    up: "like",
    //"upvote-ratio": "", TODO: ADD UPVOTE-RATIO ICON ONCE PRODUCT WANTS TO INCLUDE THIS METRIC
  },
};

function getValidScores(
  platform: Platform,
  scores: Record<string, number>,
  hideNullValues?: boolean
) {
  return compact(
    map(SCORES[platform], (icon, score) => {
      if (has(scores, score) && isNumber(scores[score])) {
        if (!hideNullValues || scores[score]) {
          return {
            score,
            count: scores[score],
            icon,
          };
        }
        return null;
      } else {
        return null;
      }
    })
  );
}

const tooltipContent = (
  platform: string,
  score: string,
  count: number,
  locale: Locale
): string =>
  `${tr(locale, `POST_METRIC_${platform}_${score}`)}: ${localize(
    locale,
    count
  )}`;

const RadarlyPostScores = ({
  post,
  locale,
  className,
  hideNullValues,
}: IProps) => {
  const lens = PostLensFactory.create(post);
  const platform = lens.getPlatform();
  const color = getPlatformColor(platform);
  return (
    <Scores color={color} className={className}>
      {map(
        getValidScores(platform, lens.getScores(), hideNullValues),
        ({ count, score, icon }) => (
          <Tooltip
            tooltip={tooltipContent(platform, score, count, locale)}
            key={score}
          >
            <Score>
              <StyledIcon glyph={icon} size={16} color={color} />
              <span>{shorten(locale, count)}</span>
            </Score>
          </Tooltip>
        )
      )}
    </Scores>
  );
};

export default RadarlyPostScores;
